import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeSlash, LockKey, CheckCircle, Warning } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';

import { getApiBaseUrl } from '../utils/api';
export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const isInvite = searchParams.get('invite') === 'true';
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }
      
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/auth/verify-reset-token?token=${token}`);
        
        if (response.ok) {
          const data = await response.json();
          setTokenValid(true);
          setTokenEmail(data.email);
        } else {
          setTokenValid(false);
        }
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          new_password: formData.password
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reset password');
      }
      
      setSuccess(true);
      toast.success('Password reset successful!');
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-muted-foreground">
            {isInvite ? 'Verifying your invitation...' : 'Verifying reset link...'}
          </p>
        </div>
      </div>
    );
  }
  
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
              alt="STAY ON Logo" 
              className="h-16 mx-auto mb-4"
            />
          </div>
          
          <Card className="bg-card border-border">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Warning size={32} weight="fill" className="text-red-500" />
              </div>
              <h2 className="text-xl font-heading text-white mb-2">
                {isInvite ? 'Invalid or Expired Invitation' : 'Invalid or Expired Link'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {isInvite 
                  ? 'This invitation link is invalid or has expired. Please contact your administrator to send a new invitation.'
                  : 'This password reset link is invalid or has expired. Please request a new one.'
                }
              </p>
              {!isInvite && (
                <Link to="/forgot-password">
                  <Button className="btn-primary" data-testid="request-new-link-btn">
                    Request New Link
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
              alt="STAY ON Logo" 
              className="h-16 mx-auto mb-4"
            />
          </div>
          
          <Card className="bg-card border-border">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} weight="fill" className="text-green-500" />
              </div>
              <h2 className="text-xl font-heading text-white mb-2">
                {isInvite ? 'Account Setup Complete!' : 'Password Reset Complete'}
              </h2>
              <p className="text-muted-foreground mb-6">
                Your password has been reset successfully. You can now login with your new password.
              </p>
              <Link to="/login">
                <Button className="btn-primary" data-testid="go-to-login-btn">
                  Go to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
            alt="STAY ON Logo" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-heading text-white mb-2">RESET PASSWORD</h1>
          <p className="text-muted-foreground">Create a new password for your account</p>
        </div>
        
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-center text-lg font-heading flex items-center justify-center gap-2">
              <LockKey size={24} className="text-primary" />
              New Password
            </CardTitle>
            <p className="text-center text-sm text-muted-foreground mt-2">
              For account: <span className="text-primary">{tokenEmail}</span>
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    minLength={8}
                    className="pr-10"
                    data-testid="new-password-input"
                    placeholder="Minimum 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    required
                    minLength={8}
                    className="pr-10"
                    data-testid="confirm-password-input"
                    placeholder="Re-enter your new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
                data-testid="reset-password-btn"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          </CardContent>
          
          <CardFooter className="flex justify-center">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
              Cancel and return to Login
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
